// src/pages/Weddings/steps/ReviewStep/PaymentPlanBox.jsx
import React, { useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Checkbox,
  Tooltip,
  Divider,
  Box,
} from "@mui/material";
import { useWeddingWizard } from "../../../../context/WeddingWizardContext";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}
function fmt(n) {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

export default function PaymentPlanBox({ previewTotals }) {
  const { state, setPaymentPreference } = useWeddingWizard();

  const pref = state.payment?.preference || {
    payNow: null,
    saveCardForAutoCharge: true,
  };

  // Only compute once the user picks an option
  const calc = useMemo(() => {
    if (pref.payNow !== "full" && pref.payNow !== "deposit50") return null;

    const itemsTotal = Number(previewTotals?.itemsTotal || 0); // non-refundable
    const withDepositsTotal = Number(previewTotals?.withDepositsTotal || 0);
    const deposits = Math.max(0, withDepositsTotal - itemsTotal); // refundable
    const fraction = pref.payNow === "full" ? 1 : 0.5;

    const dueNow = round2(deposits + itemsTotal * fraction);
    const balance = round2(withDepositsTotal - dueNow);

    return {
      itemsTotal,
      deposits,
      withDepositsTotal,
      dueNow,
      balance,
      fraction,
      payNow: pref.payNow,
    };
  }, [previewTotals, pref.payNow]);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
      <CardContent sx={{ p: 2.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          How would you like to pay today?
        </Typography>

        <RadioGroup
          value={pref.payNow || ""}
          onChange={(e) => setPaymentPreference({ payNow: e.target.value })}>
          <FormControlLabel
            value="deposit50"
            control={<Radio />}
            label="50% deposit (non-refundable) + any refundable item deposits"
          />
          <FormControlLabel
            value="full"
            control={<Radio />}
            label="Pay in full (100% of non-refundable) + any refundable item deposits"
          />
        </RadioGroup>

        {/* Only show “save card” and the breakdown AFTER a choice is made */}
        {pref.payNow ? (
          <>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 1 }}>
              <Checkbox
                checked={!!pref.saveCardForAutoCharge}
                onChange={(e) =>
                  setPaymentPreference({
                    saveCardForAutoCharge: e.target.checked,
                  })
                }
              />
              <Tooltip title="We never store your card details. Stripe keeps them securely to charge your remaining balance one week before your event.">
                <Typography variant="body2">
                  Save card with Stripe to auto-charge remaining balance 7 days
                  before the event
                </Typography>
              </Tooltip>
            </Stack>

            {calc && (
              <>
                <Divider sx={{ my: 1.25 }} />

                <Box sx={{ display: "grid", rowGap: 0.5 }}>
                  <Row
                    label="Order total"
                    value={fmt(calc.withDepositsTotal)}
                    strong
                  />
                  <Row
                    label="Includes refundable deposits"
                    value={fmt(calc.deposits)}
                    muted
                  />
                  <Row
                    label="Non-refundable subtotal"
                    value={fmt(calc.itemsTotal)}
                    muted
                  />

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    You’ll be charged now
                  </Typography>
                  <Row
                    label={
                      calc.payNow === "full"
                        ? "100% of non-refundable + all refundable deposits"
                        : "50% of non-refundable + all refundable deposits"
                    }
                    value={fmt(calc.dueNow)}
                    strong
                  />

                  {calc.balance > 0 && (
                    <Row
                      label="Balance due later"
                      value={`${fmt(calc.balance)} (7 days before event${
                        pref.saveCardForAutoCharge ? " — auto-charged" : ""
                      })`}
                    />
                  )}
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}>
                  “Refundable deposits” are returned after rentals are returned
                  in good condition. “Non-refundable” covers your
                  cake/desserts/services.
                </Typography>
              </>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Choose an option above to see what you’ll pay now and what, if
            anything, will be due later.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, value, strong, muted }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      sx={{ opacity: muted ? 0.75 : 1 }}>
      <Typography
        variant={strong ? "body1" : "body2"}
        sx={{ fontWeight: strong ? 700 : 400 }}>
        {label}
      </Typography>
      <Typography
        variant={strong ? "body1" : "body2"}
        sx={{ fontWeight: strong ? 700 : 400 }}>
        {value}
      </Typography>
    </Stack>
  );
}
